import { useQuery } from '@tanstack/react-query';
import { Suspense, useCallback, useEffect, useState } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation } from 'react-router';
import { client, unwrap } from '../api/client';
import { loggedOutOnPurpose, useAuth } from '../auth/AuthProvider';
import { useCan } from '../auth/permissions';
import { Icon } from '../ui/icons';
import { Badge, Button, Empty, Loading, PageHeader, useToast, Wordmark } from '../ui/ui';
import { NAVIGATION, navRequirement, type NavGroup, type NavItem } from './navigation';

/** Ítems que solo se resaltan con la ruta exacta (otro ítem cuelga de ellos: /crm y /crm/activities). */
const ALL_PATHS = NAVIGATION.flatMap((group) => group.items.map((item) => item.to));
const EXACT_PATHS = new Set(
  ALL_PATHS.filter(
    (path) => path === '/' || ALL_PATHS.some((other) => other.startsWith(`${path}/`)),
  ),
);
import { useRealtimeConnection } from '../lib/realtime';
import { NotificationBell } from './NotificationBell';

const NAV_STORAGE_KEY = 'eathersales.nav';
const NAV_MOBILE_KEY = 'eathersales.nav.mobile';
/** Mismo corte en que el CSS pasa el menú encima del contenido. */
const SMALL_SCREEN = '(max-width: 860px)';

const isSmallScreen = () =>
  typeof window !== 'undefined' && window.matchMedia?.(SMALL_SCREEN).matches === true;

/**
 * Preferencia del menú lateral (visible u oculto), recordada por navegador. En pantallas
 * pequeñas el menú queda encima del contenido: arranca cerrado, se recuerda aparte y se cierra
 * al elegir una opción, para que la página no empiece con todo el menú desplegado.
 */
function useNavOpen(): [boolean, () => void, () => void] {
  const [open, setOpen] = useState(() => {
    try {
      return isSmallScreen()
        ? localStorage.getItem(NAV_MOBILE_KEY) === 'open'
        : localStorage.getItem(NAV_STORAGE_KEY) !== 'closed';
    } catch {
      return !isSmallScreen();
    }
  });
  const toggle = () =>
    setOpen((current) => {
      try {
        localStorage.setItem(
          isSmallScreen() ? NAV_MOBILE_KEY : NAV_STORAGE_KEY,
          current ? 'closed' : 'open',
        );
      } catch {
        /* sin almacenamiento: la preferencia dura solo la sesión */
      }
      return !current;
    });
  const closeOnSmallScreen = useCallback(() => {
    if (isSmallScreen()) setOpen(false);
  }, []);
  return [open, toggle, closeOnSmallScreen];
}

type NavMode = 'full' | 'groups';
const NAV_MODE_KEY = 'eathersales.nav.mode';
const NAV_GROUPS_KEY = 'eathersales.nav.groups';

function readStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}
function writeStorage(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* sin almacenamiento: la preferencia dura solo la sesión */
  }
}

/**
 * Vista del menú, recordada por navegador: `full` muestra todos los módulos bajo el título de su
 * grupo; `groups` muestra solo los grupos y cada uno se despliega con sus módulos.
 */
function useNavMode(): [NavMode, (mode: NavMode) => void] {
  const [mode, setMode] = useState<NavMode>(() =>
    readStorage<NavMode>(NAV_MODE_KEY, 'full') === 'groups' ? 'groups' : 'full',
  );
  return [
    mode,
    (next) => {
      writeStorage(NAV_MODE_KEY, next);
      setMode(next);
    },
  ];
}

function NavGroups({ groups, pathname }: { groups: NavGroup[]; pathname: string }) {
  const activeGroup = groups.find((g) =>
    g.items.some((item) =>
      item.to === '/'
        ? pathname === '/'
        : pathname === item.to || pathname.startsWith(`${item.to}/`),
    ),
  )?.title;
  const [expanded, setExpanded] = useState<string[]>(() =>
    readStorage<string[]>(NAV_GROUPS_KEY, []),
  );
  const toggle = (title: string) =>
    setExpanded((current) => {
      const next = current.includes(title)
        ? current.filter((t) => t !== title)
        : [...current, title];
      writeStorage(NAV_GROUPS_KEY, next);
      return next;
    });
  return (
    <>
      {groups.map((group) => {
        // El grupo de la pantalla actual siempre se ve abierto.
        const open = expanded.includes(group.title) || group.title === activeGroup;
        const id = `nav-group-${group.title}`;
        return (
          <div key={group.title} className={open ? 'nav-group open' : 'nav-group'}>
            <button
              type="button"
              className={
                group.title === activeGroup ? 'nav-group-button current' : 'nav-group-button'
              }
              aria-expanded={open}
              aria-controls={id}
              onClick={() => toggle(group.title)}
            >
              <Icon name={group.icon} />
              <span>{group.title}</span>
              <Icon name="chevron" size={14} className="nav-chevron" />
            </button>
            {open && (
              <div id={id} className="nav-group-items">
                {group.items.map((item) => (
                  <NavLink key={item.to} to={item.to} end={EXACT_PATHS.has(item.to)}>
                    <Icon name={item.icon} size={16} />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

export function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <Loading rows={3} />;
  if (status === 'anonymous')
    return (
      <Navigate
        to="/login"
        replace
        state={loggedOutOnPurpose() ? undefined : { from: location.pathname }}
      />
    );
  return <AppShell />;
}

function AppShell() {
  const { me, session, switchTenant, logout } = useAuth();
  const can = useCan();
  const toast = useToast();
  const [navOpen, toggleNav, closeNavOnSmallScreen] = useNavOpen();
  const [navMode, setNavMode] = useNavMode();
  const location = useLocation();
  // En celular o tablet, elegir una opción del menú lo cierra y deja ver la página.
  useEffect(() => closeNavOnSmallScreen(), [location.pathname, closeNavOnSmallScreen]);
  const blocked = me ? BLOCKED_STATUSES.has(me.tenant.status) : false;
  // Todos se conectan: además del Inbox, por aquí llegan las notificaciones de cada persona.
  useRealtimeConnection(Boolean(me) && !blocked);
  if (!me) return <Loading rows={6} />;
  const tenants = session?.tenants ?? me.tenants;
  // La pantalla se protege con el mismo requisito de plan que su entrada del menú: entrar por la
  // dirección no muestra formularios que el plan rechazaría al guardar.
  const screen = navRequirement(location.pathname);
  // En una pantalla de detalle (/sales/orders/123), enlace para volver a su lista con los
  // filtros que tenía (se recuerda la última dirección de cada lista).
  if (screen && location.pathname === screen.to)
    lastListUrl.set(screen.to, location.pathname + location.search);
  const backTo = backLink(location.pathname, screen);
  const screenIncluded =
    !screen || can({ feature: screen.feature, platformAdmin: screen.platformAdmin });
  // Incluida en el plan pero sin el permiso de la persona (entró por la dirección): aviso en
  // palabras en vez de una pantalla en blanco o errores de permiso de cada bloque.
  const screenAllowed = !screen || can(screen);
  const visibleGroups = NAVIGATION.map((group) => ({
    ...group,
    items: group.items.filter((item) => can(item)),
  })).filter((group) => group.items.length > 0);

  return (
    <div className={navOpen ? 'shell' : 'shell nav-collapsed'}>
      <aside id="sidebar" className="sidebar" aria-label="Menú principal" inert={!navOpen}>
        <div className="brand">
          <Wordmark product={productLabel(me.tenant.productName)} />
        </div>
        <nav
          className={navMode === 'groups' ? 'nav nav-mode-groups stack' : 'nav stack'}
          style={{ gap: navMode === 'groups' ? 4 : 14 }}
        >
          {navMode === 'groups' ? (
            <NavGroups groups={visibleGroups} pathname={location.pathname} />
          ) : (
            visibleGroups.map((group) => (
              <div key={group.title}>
                <div className="nav-group-title">{group.title}</div>
                {group.items.map((item) => (
                  <NavLink key={item.to} to={item.to} end={EXACT_PATHS.has(item.to)}>
                    <Icon name={item.icon} size={16} />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            ))
          )}
        </nav>
        <div className="nav-mode" role="group" aria-label="Vista del menú">
          <span className="nav-group-title">Vista del menú</span>
          <div className="nav-mode-options">
            <button
              type="button"
              aria-pressed={navMode === 'full'}
              onClick={() => setNavMode('full')}
            >
              Completa
            </button>
            <button
              type="button"
              aria-pressed={navMode === 'groups'}
              onClick={() => setNavMode('groups')}
            >
              Por grupos
            </button>
          </div>
        </div>
      </aside>
      <div className="main">
        <div className="topbar">
          <button
            type="button"
            className="nav-toggle"
            onClick={toggleNav}
            aria-controls="sidebar"
            aria-expanded={navOpen}
            aria-label={navOpen ? 'Ocultar menú' : 'Mostrar menú'}
            title={navOpen ? 'Ocultar menú' : 'Mostrar menú'}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
              <rect
                x="1.5"
                y="2.5"
                width="15"
                height="13"
                rx="1"
                fill="none"
                stroke="currentColor"
              />
              <line x1="6.5" y1="2.5" x2="6.5" y2="15.5" stroke="currentColor" />
              <path
                d={navOpen ? 'M12 6.5 9.5 9l2.5 2.5' : 'M10 6.5 12.5 9 10 11.5'}
                fill="none"
                stroke="currentColor"
                strokeLinecap="square"
              />
            </svg>
          </button>
          {tenants.length > 1 ? (
            <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <span className="sr-only">Empresa</span>
              <select
                value={me.tenant.id}
                onChange={(e) => switchTenant(e.target.value).catch(toast.fail)}
                style={{ minWidth: 200 }}
              >
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <strong className="topbar-tenant">{me.tenant.name}</strong>
          )}
          <Badge
            className="topbar-plan"
            tone={
              me.tenant.status === 'trial'
                ? 'warn'
                : me.tenant.status === 'active'
                  ? 'brand'
                  : 'danger'
            }
          >
            {me.tenant.planName}
            {me.tenant.status === 'trial' && ' · prueba'}
            {me.tenant.status === 'suspended' && ' · suspendida'}
            {me.tenant.status === 'trial_expired' && ' · prueba vencida'}
          </Badge>
          <span className="spacer" />
          {!blocked && <NotificationBell title={me.tenant.productName ?? undefined} />}
          <HelpButton pathname={location.pathname} />
          <span className="topbar-user" title={me.user.email}>
            <span className="muted">{me.user.fullName}</span>
            {me.roleName && <span className="topbar-user-role">{me.roleName}</span>}
          </span>
          <Button size="sm" onClick={() => void logout()}>
            Salir
          </Button>
        </div>
        <main className="content">
          {!me.user.emailVerified && <VerifyEmailNotice email={me.user.email} />}
          {me.tenant.status === 'suspended' && (
            <div className="alert" style={{ marginBottom: 16 }}>
              La cuenta está suspendida. Ponte al día con la suscripción en «Plan y consumo» para
              seguir operando.
            </div>
          )}
          <Suspense fallback={<Loading />}>
            {blocked && !OPEN_WHEN_BLOCKED.some((path) => location.pathname.startsWith(path)) ? (
              <SubscriptionRequired
                status={me.tenant.status}
                canChoosePlan={can({ perm: 'billing.read' })}
              />
            ) : !screenIncluded ? (
              <NotInPlan
                label={screen!.label}
                feature={screen!.feature}
                productName={me.tenant.productName}
                canChoosePlan={can({ perm: 'billing.read' })}
              />
            ) : !screenAllowed ? (
              <Empty title={`No tienes permiso para ver ${screen!.label}`}>
                Tu rol no incluye esta pantalla. Si la necesitas, pídele a quien administra la
                cuenta que te dé acceso en Configuración → Roles y permisos.
              </Empty>
            ) : (
              <>
                {backTo && (
                  <Link to={backTo.to} className="back-link">
                    ← {backTo.label}
                  </Link>
                )}
                <Outlet />
              </>
            )}
          </Suspense>
        </main>
      </div>
    </div>
  );
}

/** Última dirección (con filtros) de cada lista del menú, para el enlace «← Lista». */
const lastListUrl = new Map<string, string>();

/** Pantallas que muestran su lista al lado (el Inbox): no necesitan «← Lista». */
const SPLIT_VIEWS = new Set(['/inbox']);
/** Al editar se vuelve al documento, no a la lista. */
const EDIT_BACK_LABEL: Record<string, string> = {
  '/sales/orders': 'Volver al pedido',
  '/purchasing/orders': 'Volver a la orden',
};

function backLink(path: string, screen: NavItem | null): { to: string; label: string } | null {
  if (!screen || path === screen.to || SPLIT_VIEWS.has(screen.to)) return null;
  if (path.endsWith('/edit'))
    return { to: path.slice(0, -'/edit'.length), label: EDIT_BACK_LABEL[screen.to] ?? 'Volver' };
  return { to: lastListUrl.get(screen.to) ?? screen.to, label: screen.label };
}

/** «EATHERIX CRM» → «CRM»: el wordmark ya dice EATHERIX. */
function productLabel(name: string | null): string | undefined {
  return name?.replace(/^EATHERIX\s+/i, '') || undefined;
}

/** Pantalla de un módulo que el plan de la empresa no incluye. */
function NotInPlan({
  label,
  feature,
  productName,
  canChoosePlan,
}: {
  label: string;
  feature?: string | string[];
  productName: string | null;
  canChoosePlan: boolean;
}) {
  // Qué es el módulo, para que cualquiera entienda qué le falta (misma frase de Plan y consumo).
  const catalog = useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: () => unwrap(client.GET('/v1/billing/plans')),
    enabled: Boolean(feature),
  });
  const keys = feature === undefined ? [] : Array.isArray(feature) ? feature : [feature];
  const description = keys.map((key) => catalog.data?.featureDescriptions[key]).find(Boolean);
  return (
    <Empty title={`${label} no está incluido en tu plan`}>
      {description ? (
        <>
          {description}
          <br />
        </>
      ) : null}
      {productName ? `Tu empresa tiene ${productName}. ` : ''}
      {canChoosePlan ? (
        <>
          Puedes ver qué incluye cada plan en <Link to="/settings/billing">Plan y consumo</Link>.
        </>
      ) : (
        'Pídele a quien administra la cuenta que revise el plan.'
      )}
    </Empty>
  );
}

/** Correo sin confirmar: no bloquea, pero sin él los avisos de la cuenta no llegan a nadie. */
function VerifyEmailNotice({ email }: { email: string }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const resend = async () => {
    setBusy(true);
    try {
      await unwrap(client.POST('/v1/auth/resend-verification'));
      setSent(true);
    } catch (error) {
      toast.fail(error);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="alert alert-info verify-email-notice" role="status">
      <span>
        {sent
          ? `Te enviamos un enlace nuevo a ${email}. Revisa también la carpeta de spam.`
          : `Confirma tu correo (${email}) con el enlace que te enviamos para recibir los avisos de tu cuenta y de tu prueba.`}
      </span>
      {!sent && (
        <Button busy={busy} onClick={() => void resend()}>
          Reenviar correo
        </Button>
      )}
    </div>
  );
}

/** Estados en que la empresa no opera (como `BLOCKED_TENANT_STATUSES` en la API). */
const BLOCKED_STATUSES = new Set(['trial_expired', 'suspended', 'cancelled']);
/** Pantallas disponibles mientras tanto: elegir plan, ayuda, soporte y la propia seguridad. */
const OPEN_WHEN_BLOCKED = ['/settings/billing', '/help', '/support', '/settings/security'];

/** Prueba vencida o cuenta suspendida: lleva a elegir plan (los datos se conservan). */
function SubscriptionRequired({
  status,
  canChoosePlan,
}: {
  status: string;
  canChoosePlan: boolean;
}) {
  const expired = status === 'trial_expired';
  return (
    <div className="stack" style={{ maxWidth: 560 }}>
      <PageHeader title={expired ? 'Tu prueba gratuita terminó' : 'La cuenta está suspendida'} />
      <p>
        {expired
          ? 'Elige un plan para seguir usando EATHERIX Sales. Todo lo que registraste durante la prueba se conserva.'
          : 'Ponte al día con la suscripción para seguir operando. Tus datos se conservan.'}
      </p>
      {canChoosePlan ? (
        <div className="actions">
          <NavLink to="/settings/billing" className="btn btn-primary">
            Elegir plan
          </NavLink>
        </div>
      ) : (
        <p className="muted">
          Pídele al propietario de la empresa que elija un plan en «Plan y consumo».
        </p>
      )}
    </div>
  );
}

/**
 * Ayuda contextual: abre el Centro de ayuda con la pantalla actual; allí se elige su artículo.
 * (Los artículos se cargan solo en el Centro de ayuda, no en la carga inicial del panel.)
 */
function HelpButton({ pathname }: { pathname: string }) {
  return (
    <NavLink
      to={`/help?desde=${encodeURIComponent(pathname)}`}
      className="btn btn-sm"
      title="Ayuda sobre esta pantalla"
      aria-label="Ayuda sobre esta pantalla"
    >
      ? Ayuda
    </NavLink>
  );
}
